import React, { useState } from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Pencil, X } from "lucide-react";
import InputField from "../../form/InputField";
import { PermissionGate } from "../PermissionGate";
import { PermissionsKey } from "../../../data/permissions";

export interface ContactInfoFormData {
  phoneNumber: string;
  email: string;
  country: string;
}

interface Props {
  register: UseFormRegister<ContactInfoFormData>;
  errors: FieldErrors<ContactInfoFormData>;
  defaultValues?: Partial<ContactInfoFormData>;
  onSave?: () => void;
}

export default function ContactInfoForm({ register, errors, defaultValues }: Props) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const handleToggleEdit = () => setIsEditing((prev) => !prev);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t("contact_info")}</h3>
          {!isEditing && (
            <p className="text-sm text-gray-500 mt-1">
              {t("click_edit_to_update_values")}
            </p>
          )}
        </div>

        <PermissionGate permissions={[PermissionsKey.SystemSettings]}>
          <button
            type="button"
            onClick={handleToggleEdit}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition font-medium ${
              isEditing
                ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                : "bg-primary text-white hover:opacity-90"
            }`}
          >
            {isEditing ? (
              <>
                <X size={16} /> {t("cancel_edit")}
              </>
            ) : (
              <>
                <Pencil size={16} /> {t("edit")}
              </>
            )}
          </button>
        </PermissionGate>
      </div>

      {/* Form Fields */}
      <InputField
        label={t("phone")}
        type="text"
        placeholder="+966 500000000"
        readOnly={!isEditing}
        defaultValue={defaultValues?.phoneNumber || ""}
        register={register("phoneNumber", { required: t("required_field") as string })}
        error={errors.phoneNumber}
      />

      <InputField
        label={t("email")}
        type="email"
        placeholder="example@email.com"
        readOnly={!isEditing}
        defaultValue={defaultValues?.email || ""}
        register={register("email", { required: t("required_field") as string })}
        error={errors.email}
      />

      <InputField
        label={t("city")}
        type="text"
        placeholder={t("enter_city") || ""}
        readOnly={!isEditing}
        defaultValue={defaultValues?.country || ""}
        register={register("country", { required: t("required_field") as string })}
        error={errors.country}
      />
    </div>
  );
}
